import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer'

const PALETTE = [
  { hex: '#2c5aa0', rgb: '44, 90, 160' },
  { hex: '#059669', rgb: '5, 150, 105' },
  { hex: '#d97706', rgb: '217, 119, 6' },
  { hex: '#7c3aed', rgb: '124, 58, 237' },
  { hex: '#e11d48', rgb: '225, 29, 72' },
]

const getStatusFromScore = (score) => {
  if (score <= 2) return { color: '#dc3545', text: 'CRITIQUE' }
  if (score <= 5) return { color: '#fd7e14', text: 'À AMÉLIORER' }
  if (score <= 7) return { color: '#ffc107', text: 'SATISFAISANT' }
  if (score <= 9) return { color: '#28a745', text: 'BON' }
  return { color: '#198754', text: 'EXCELLENCE' }
}

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })

const truncate = (text, max) => (text.length > max ? `${text.substring(0, max)}…` : text)

const personName = (a) => {
  const first = a?.clientInfo?.firstName?.trim() || ''
  const last = a?.clientInfo?.lastName?.trim() || ''
  return `${first} ${last}`.trim()
}

/** "Prénom Nom - Entreprise", used wherever there is room for the full label. */
const personCompanyLabel = (a) => {
  const name = personName(a)
  const company = a?.clientInfo?.company?.trim() || ''
  if (name && company) return `${name} - ${company}`
  return name || company || 'Évaluation'
}

/** "P. Nom" - fits the narrow per-assessment table columns. */
const abbrevPersonLabel = (a) => {
  const first = a?.clientInfo?.firstName?.trim() || ''
  const last = a?.clientInfo?.lastName?.trim() || ''
  if (first && last) return truncate(`${first.charAt(0)}. ${last}`, 14)
  return truncate(last || first || a?.clientInfo?.company?.trim() || '', 14)
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 40,
    paddingBottom: 60,
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 25,
    paddingBottom: 15,
    borderBottomWidth: 3,
    borderBottomColor: '#2c5aa0',
    alignItems: 'center',
  },
  logoImage: {
    width: 120,
    height: 'auto',
    marginBottom: 12,
    objectFit: 'contain',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2c5aa0',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 11,
    color: '#666666',
    marginBottom: 2,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c5aa0',
    marginBottom: 12,
    paddingBottom: 4,
    borderBottomWidth: 2,
    borderBottomColor: '#2c5aa0',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 8,
    color: '#999999',
    flexShrink: 0,
  },
  // The company list can be long, so it takes the leftover width and gets an
  // ellipsis instead of overlapping the left half of the footer.
  footerCompanies: {
    fontSize: 8,
    color: '#999999',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    paddingLeft: 8,
    textAlign: 'right',
    textOverflow: 'ellipsis',
  },

  assessmentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
    padding: 10,
    marginBottom: 6,
    borderLeftWidth: 4,
  },
  cardBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  cardBadgeText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  cardInfo: {
    flex: 1,
  },
  cardName: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#333333',
  },
  cardDetails: {
    fontSize: 9,
    color: '#666666',
    marginTop: 1,
  },
  cardScore: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2c5aa0',
    marginLeft: 10,
  },

  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 8,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  legendDot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    marginRight: 4,
  },
  legendText: {
    fontSize: 8,
    color: '#333333',
  },

  table: {
    marginTop: 8,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#2c5aa0',
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  tableHeaderCell: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 5,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  tableRowAlt: {
    backgroundColor: '#f8f9fa',
  },
  tableCell: {
    fontSize: 9,
    color: '#333333',
    textAlign: 'center',
  },
  tableCellLeft: {
    fontSize: 9,
    color: '#333333',
    textAlign: 'left',
  },

  statsRow: {
    flexDirection: 'row',
    marginTop: 10,
    gap: 10,
  },
  statBox: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    padding: 8,
    alignItems: 'center',
    borderLeftWidth: 3,
    borderLeftColor: '#2c5aa0',
  },
  statValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#2c5aa0',
  },
  statLabel: {
    fontSize: 8,
    color: '#666666',
    marginTop: 2,
  },

  themeBlock: {
    marginBottom: 14,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  themeTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 6,
  },
  themeStats: {
    fontSize: 8,
    color: '#666666',
    marginBottom: 6,
  },

  questionBlock: {
    marginBottom: 8,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  questionText: {
    fontSize: 9,
    color: '#333333',
    marginBottom: 4,
    lineHeight: 1.3,
  },
  scoresRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  scorePill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 4,
  },
  scorePillDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 4,
  },
  scorePillText: {
    fontSize: 8,
    fontWeight: 'bold',
  },

  deltaPositive: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#059669',
  },
  deltaNegative: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#dc3545',
  },
  deltaNeutral: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#666666',
  },
})

const DeltaText = ({ value }) => {
  if (value === null || value === undefined) return null
  const sign = value > 0 ? '+' : ''
  const style = value > 0 ? styles.deltaPositive : value < 0 ? styles.deltaNegative : styles.deltaNeutral
  return <Text style={style}>{sign}{value.toFixed(1)}</Text>
}

/** Colour legend mapping each palette colour to "n. Prénom Nom - Entreprise". */
const AssessmentLegend = ({ assessments }) => (
  <View style={styles.legendRow}>
    {assessments.map((a, i) => {
      const color = PALETTE[i % PALETTE.length]
      return (
        <View key={a.id} style={styles.legendItem}>
          <View style={{ ...styles.legendDot, backgroundColor: color.hex }} />
          <Text style={styles.legendText}>{i + 1}. {personCompanyLabel(a)}</Text>
        </View>
      )
    })}
  </View>
)

const CEDECComparisonReport = ({ assessments, comparison }) => {
  const n = assessments.length
  const matchedThemes = comparison.themes.filter(t => t.matched)
  const today = new Date().toLocaleDateString('fr-FR')
  const companies = [...new Set(assessments.map(a => a.clientInfo.company))]
  const companyLabel = companies.join(' / ')
  // The footer sits on one line next to the generation date, so its copy of the
  // company list is trimmed to what the remaining half of the width can hold.
  const footerLabel = truncate(companyLabel, 60)

  return (
    <Document
      title={`Comparaison CEDEC - ${companyLabel}`}
      creator="CEDEC"
      producer="CEDEC Assessment Tool"
    >
      {/* ===== PAGE 1: SUMMARY ===== */}
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header} fixed>
          <Image
            src="https://cedecfrblob.blob.core.windows.net/public/Cedec-logo-nosub%20(1).png?sp=r&st=2025-10-01T20:52:55Z&se=2035-10-02T05:07:55Z&spr=https&sv=2024-11-04&sr=b&sig=Tuc%2B%2F4KITu7WsV%2BBY%2FAciyNhGxVOdoumFoSNH39Azqs%3D"
            style={styles.logoImage}
          />
          <Text style={styles.title}>Rapport Comparatif</Text>
          <Text style={styles.subtitle}>Score de Résilience - Comparaison de {n} évaluations</Text>
          <Text style={styles.subtitle}>Généré le {today}</Text>
        </View>

        {/* Assessment cards */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Évaluations comparées</Text>
          {assessments.map((a, i) => {
            const color = PALETTE[i % PALETTE.length]
            return (
              <View key={a.id} style={{ ...styles.assessmentCard, borderLeftColor: color.hex }} wrap={false}>
                <View style={{ ...styles.cardBadge, backgroundColor: color.hex }}>
                  <Text style={styles.cardBadgeText}>{i + 1}</Text>
                </View>
                <View style={styles.cardInfo}>
                  <Text style={styles.cardName}>
                    {personCompanyLabel(a)}
                  </Text>
                  <Text style={styles.cardDetails}>
                    {formatDate(a.clientInfo.assessmentDate)} · {a.completedQuestions}/{a.totalQuestions} questions
                    {a.questionnaireVersion ? ` · v${a.questionnaireVersion}` : ''}
                  </Text>
                </View>
                <Text style={{ ...styles.cardScore, color: color.hex }}>
                  {a.averageScore.toFixed(1)}/10
                </Text>
              </View>
            )
          })}
        </View>

        {/* Overall stats */}
        <View style={styles.statsRow} wrap={false}>
          <View style={styles.statBox}>
            <Text style={styles.statValue}>{comparison.overall.stats.avg?.toFixed(1) ?? '--'}</Text>
            <Text style={styles.statLabel}>Moyenne générale</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statValue}>{comparison.overall.stats.min?.toFixed(1) ?? '--'}</Text>
            <Text style={styles.statLabel}>Score minimum</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statValue}>{comparison.overall.stats.max?.toFixed(1) ?? '--'}</Text>
            <Text style={styles.statLabel}>Score maximum</Text>
          </View>
          <View style={styles.statBox}>
            <Text style={styles.statValue}>
              {comparison.overall.stats.min !== null
                ? (comparison.overall.stats.max - comparison.overall.stats.min).toFixed(1)
                : '--'}
            </Text>
            <Text style={styles.statLabel}>Écart</Text>
          </View>
        </View>

        {/* Theme scores table */}
        <View style={{ ...styles.section, marginTop: 20 }}>
          <Text style={styles.sectionTitle}>Synthèse par thème</Text>

          <AssessmentLegend assessments={assessments} />

          <View style={styles.table}>
            <View style={styles.tableHeader} wrap={false}>
              <Text style={{ ...styles.tableHeaderCell, flex: 3, textAlign: 'left' }}>Thème</Text>
              {assessments.map((a, i) => (
                <Text key={a.id} style={{ ...styles.tableHeaderCell, flex: 1 }}>
                  {/* Beyond 3 assessments the columns are too narrow for a name,
                      so the numbered legend above the table carries the mapping. */}
                  {n <= 3 ? `${i + 1}. ${abbrevPersonLabel(a)}` : `Éval. ${i + 1}`}
                </Text>
              ))}
              <Text style={{ ...styles.tableHeaderCell, flex: 1 }}>Écart</Text>
              <Text style={{ ...styles.tableHeaderCell, flex: 1 }}>Moy.</Text>
            </View>

            {matchedThemes.map((theme, idx) => (
              <View
                key={theme.id}
                style={{
                  ...styles.tableRow,
                  ...(idx % 2 === 1 ? styles.tableRowAlt : {}),
                }}
                wrap={false}
              >
                <Text style={{ ...styles.tableCellLeft, flex: 3 }}>{theme.title}</Text>
                {theme.scores.map((score, i) => {
                  const status = score !== null ? getStatusFromScore(score) : null
                  return (
                    <Text
                      key={i}
                      style={{
                        ...styles.tableCell,
                        flex: 1,
                        color: status ? status.color : '#999',
                        fontWeight: 'bold',
                      }}
                    >
                      {score !== null ? `${score.toFixed(1)}` : 'N/A'}
                    </Text>
                  )
                })}
                <Text style={{ ...styles.tableCell, flex: 1 }}>
                  {theme.stats.min !== null
                    ? (theme.stats.max - theme.stats.min).toFixed(1)
                    : '--'}
                </Text>
                <Text style={{ ...styles.tableCell, flex: 1, fontWeight: 'bold' }}>
                  {theme.stats.avg !== null ? theme.stats.avg.toFixed(1) : '--'}
                </Text>
              </View>
            ))}

            {/* Overall row */}
            <View style={{ ...styles.tableRow, backgroundColor: '#e8f0fe', borderTopWidth: 2, borderTopColor: '#2c5aa0' }} wrap={false}>
              <Text style={{ ...styles.tableCellLeft, flex: 3, fontWeight: 'bold', color: '#2c5aa0' }}>
                SCORE GLOBAL
              </Text>
              {comparison.overall.scores.map((score, i) => (
                <Text key={i} style={{ ...styles.tableCell, flex: 1, fontWeight: 'bold', color: '#2c5aa0' }}>
                  {score.toFixed(1)}
                </Text>
              ))}
              <Text style={{ ...styles.tableCell, flex: 1, fontWeight: 'bold', color: '#2c5aa0' }}>
                {comparison.overall.stats.min !== null
                  ? (comparison.overall.stats.max - comparison.overall.stats.min).toFixed(1)
                  : '--'}
              </Text>
              <Text style={{ ...styles.tableCell, flex: 1, fontWeight: 'bold', color: '#2c5aa0' }}>
                {comparison.overall.stats.avg?.toFixed(1) ?? '--'}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Rapport comparatif généré le {today} par CEDEC</Text>
          <Text style={styles.footerCompanies}>{footerLabel} - Confidentiel</Text>
        </View>
      </Page>

      {/* ===== PAGE 2+: QUESTION DETAILS ===== */}
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Détail par thème et question</Text>
          <Text style={styles.subtitle}>
            {comparison.stats.matchedThemes} thèmes correspondants sur {comparison.stats.totalThemes}
          </Text>
          <View style={{ marginTop: 8 }}>
            <AssessmentLegend assessments={assessments} />
          </View>
        </View>

        {matchedThemes.map((theme) => {
          const allQuestionIds = new Set()
          const questionMeta = new Map()
          assessments.forEach((a, i) => {
            const themeId = theme.themeIds?.[i] ?? theme.id
            const t = themeId ? a.themes?.find(t2 => t2.id === themeId) : null
            if (t?.questions) {
              t.questions.forEach(q => {
                allQuestionIds.add(q.id)
                if (!questionMeta.has(q.id)) questionMeta.set(q.id, q)
              })
            }
          })

          return (
            <View key={theme.id} style={styles.themeBlock} wrap={false}>
              <Text style={styles.themeTitle}>
                {theme.title}
              </Text>
              <Text style={styles.themeStats}>
                Moyenne : {theme.stats.avg?.toFixed(1) ?? '--'}/10 · Écart : {theme.stats.min !== null ? (theme.stats.max - theme.stats.min).toFixed(1) : '--'}
              </Text>

              {Array.from(allQuestionIds).map(qId => {
                const meta = questionMeta.get(qId)
                const scores = assessments.map(a => a.scores?.[qId] ?? null)

                return (
                  <View key={qId} style={styles.questionBlock}>
                    <Text style={styles.questionText}>{meta?.title || qId}</Text>
                    <View style={styles.scoresRow}>
                      {scores.map((score, i) => {
                        const color = PALETTE[i % PALETTE.length]
                        return (
                          <View
                            key={i}
                            style={{
                              ...styles.scorePill,
                              backgroundColor: `rgba(${color.rgb}, 0.12)`,
                            }}
                          >
                            <View style={{ ...styles.scorePillDot, backgroundColor: color.hex }} />
                            <Text style={{ ...styles.scorePillText, color: color.hex }}>
                              {score !== null ? `${score}/10` : 'N/A'}
                            </Text>
                          </View>
                        )
                      })}
                      {scores.length >= 2 && scores[0] !== null && scores[scores.length - 1] !== null && (
                        <DeltaText value={scores[scores.length - 1] - scores[0]} />
                      )}
                    </View>
                  </View>
                )
              })}
            </View>
          )
        })}

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Rapport comparatif généré le {today} par CEDEC</Text>
          <Text style={styles.footerCompanies}>{footerLabel} - Confidentiel</Text>
        </View>
      </Page>
    </Document>
  )
}

export default CEDECComparisonReport
